import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import ConnectionCard from './components/ConnectionCard';
import FilterPanel from './components/FilterPanel';
import NetworkInsights from './components/NetworkInsights';
import RecommendationReasons from './components/RecommendationReasons';
import { supabase } from '../../lib/supabase';

const ConnectionRecommendations = () => {
  const [recommendations, setRecommendations] = useState([]);
  const [filteredRecommendations, setFilteredRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    role: 'all',
    location: 'all',
    profession: 'all',
    experienceLevel: 'all',
    verifiedOnly: false
  });
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    getCurrentUser();
    fetchRecommendations();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [recommendations, filters]);

  const getCurrentUser = async () => {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (session?.user) {
        setCurrentUser(session?.user);
      }
    } catch (error) {
      console.error('Error getting current user:', error);
    }
  };

  const fetchRecommendations = async () => {
    try {
      setLoading(true);

      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.user) return;

      // Get existing connections to exclude
      const { data: existingConnections } = await supabase?.from('professional_connections')?.select('requester_id, recipient_id')?.or(`requester_id.eq.${session?.user?.id},recipient_id.eq.${session?.user?.id}`);

      const connectedUserIds = new Set();
      existingConnections?.forEach(conn => {
        connectedUserIds?.add(conn?.requester_id);
        connectedUserIds?.add(conn?.recipient_id);
      });

      // Fetch potential connections with comprehensive data
      const { data, error } = await supabase?.from('user_profiles')?.select(`
          id,
          full_name,
          avatar_url,
          role,
          created_at,
          partners (
            id,
            business_name,
            partner_type,
            artist_profession,
            city,
            country,
            is_verified,
            total_bookings,
            average_rating,
            years_experience,
            languages,
            description,
            awards
          ),
          partner_social_links (
            platform,
            follower_count,
            is_verified
          ),
          partner_musical_specialties (
            specialty
          )
        `)?.neq('id', session?.user?.id);

      if (error) throw error;

      // Filter out existing connections and process data
      const processedRecommendations = (data || [])?.filter(user => !connectedUserIds?.has(user?.id))?.map(user => ({
          ...user,
          // Calculate match score based on various factors
          matchScore: calculateMatchScore(user),
          // Add mutual connections count (mock for now)
          mutualConnections: Math.floor(Math.random() * 5),
          // Add recommendation reasons
          recommendationReasons: generateRecommendationReasons(user)
        }))?.sort((a, b) => b?.matchScore - a?.matchScore);

      setRecommendations(processedRecommendations);
    } catch (error) {
      console.error('Error fetching recommendations:', error);
      setRecommendations([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateMatchScore = (user) => {
    let score = 50; // Base score

    const partner = user?.partners?.[0];
    if (!partner) return score;

    // Verified accounts get higher scores
    if (partner?.is_verified) score += 20;

    // Higher ratings increase score
    if (partner?.average_rating > 4) score += 15;
    else if (partner?.average_rating > 3.5) score += 10;

    // Experience level
    if (partner?.years_experience > 5) score += 10;
    else if (partner?.years_experience > 2) score += 5;

    // Active users (total bookings)
    if (partner?.total_bookings > 10) score += 15;
    else if (partner?.total_bookings > 5) score += 10;

    // Social media presence
    const socialLinks = user?.partner_social_links || [];
    const totalFollowers = socialLinks?.reduce((sum, link) => sum + (link?.follower_count || 0), 0);
    if (totalFollowers > 10000) score += 10;
    else if (totalFollowers > 1000) score += 5;

    return Math.min(score, 100); // Cap at 100
  };

  const generateRecommendationReasons = (user) => {
    const reasons = [];
    const partner = user?.partners?.[0];
    
    if (!partner) return reasons;

    if (partner?.is_verified) {
      reasons?.push({ type: 'verified', text: 'Profil vérifié' });
    }

    if (partner?.city) {
      reasons?.push({ type: 'location', text: `Basé à ${partner?.city}` });
    }

    if (partner?.partner_type === 'agent') {
      reasons?.push({ type: 'role', text: 'Agent professionnel' });
    }

    if (partner?.total_bookings > 10) {
      reasons?.push({ type: 'activity', text: 'Très actif sur la plateforme' });
    }

    if (partner?.artist_profession) {
      const professions = {
        'musician': 'Musicien',
        'singer': 'Chanteur',
        'dj': 'DJ',
        'producer': 'Producteur'
      };
      reasons?.push({ 
        type: 'profession', 
        text: professions?.[partner?.artist_profession] || partner?.artist_profession 
      });
    }

    return reasons?.slice(0, 3); // Limit to 3 reasons
  };

  const applyFilters = () => {
    let filtered = [...recommendations];

    // Role filter
    if (filters?.role !== 'all') {
      filtered = filtered?.filter(user => {
        const partner = user?.partners?.[0];
        return partner?.partner_type === filters?.role;
      });
    }

    // Location filter
    if (filters?.location !== 'all') {
      filtered = filtered?.filter(user => {
        const partner = user?.partners?.[0];
        return partner?.city === filters?.location;
      });
    }

    // Profession filter
    if (filters?.profession !== 'all') {
      filtered = filtered?.filter(user => {
        const partner = user?.partners?.[0];
        return partner?.artist_profession === filters?.profession;
      });
    }

    // Experience level filter
    if (filters?.experienceLevel !== 'all') {
      filtered = filtered?.filter(user => {
        const partner = user?.partners?.[0];
        const experience = partner?.years_experience || 0;
        
        switch (filters?.experienceLevel) {
          case 'beginner': return experience <= 2;
          case 'intermediate': return experience > 2 && experience <= 5;
          case 'expert': return experience > 5;
          default: return true;
        }
      });
    }

    // Verified only filter
    if (filters?.verifiedOnly) {
      filtered = filtered?.filter(user => {
        const partner = user?.partners?.[0];
        return partner?.is_verified;
      });
    }

    setFilteredRecommendations(filtered);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleConnectionRequest = (userId) => {
    // Update recommendations to remove the user after connection request
    setRecommendations(prev => prev?.filter(user => user?.id !== userId));
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Recommandations de Connexions - Artis.io</title>
        <meta name="description" content="Découvrez et connectez-vous avec des professionnels de l'industrie musicale et artistique" />
      </Helmet>
      <Header />
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="py-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground">Connexions Recommandées</h1>
                <p className="mt-2 text-muted-foreground">
                  Découvrez des professionnels qui pourraient vous intéresser et élargissez votre réseau artistique
                </p>
              </div>
              
              <div className="mt-4 lg:mt-0 flex items-center space-x-4">
                {/* View Mode Toggle */}
                <div className="flex items-center bg-muted rounded-lg p-1">
                  <Button
                    variant={viewMode === 'grid' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('grid')}
                    iconName="Grid"
                    className="px-3"
                  />
                  <Button
                    variant={viewMode === 'list' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('list')}
                    iconName="List"
                    className="px-3"
                  />
                </div>

                {/* Results Count */}
                <div className="text-sm text-muted-foreground">
                  {filteredRecommendations?.length} recommandation{filteredRecommendations?.length > 1 ? 's' : ''}
                </div>
              </div>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 pb-8">
            
            {/* Left Sidebar - Filters & Insights */}
            <div className="lg:col-span-3 space-y-6">
              <FilterPanel
                filters={filters}
                onFiltersChange={handleFilterChange}
                totalRecommendations={recommendations?.length}
              />
              <NetworkInsights />
            </div>

            {/* Main Content Area */}
            <div className="lg:col-span-6">
              {loading ? (
                <div className={`grid ${viewMode === 'grid' ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'} gap-6`}>
                  {[1, 2, 3, 4, 5, 6]?.map((i) => (
                    <div key={i} className="bg-surface rounded-lg border border-border p-6">
                      <div className="animate-pulse">
                        <div className="flex items-center space-x-4 mb-4">
                          <div className="w-16 h-16 bg-muted rounded-full"></div>
                          <div className="flex-1 space-y-2">
                            <div className="h-4 bg-muted rounded w-3/4"></div>
                            <div className="h-3 bg-muted rounded w-1/2"></div>
                          </div>
                        </div>
                        <div className="space-y-3">
                          <div className="h-3 bg-muted rounded w-full"></div>
                          <div className="h-3 bg-muted rounded w-2/3"></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : filteredRecommendations?.length > 0 ? (
                <div className={`grid ${viewMode === 'grid' ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'} gap-6`}>
                  {filteredRecommendations?.map((user) => (
                    <ConnectionCard
                      key={user?.id}
                      user={user}
                      viewMode={viewMode}
                      onConnectionRequest={handleConnectionRequest}
                    />
                  ))}
                </div>
              ) : (
                <div className="bg-surface rounded-lg border border-border p-8 text-center">
                  <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl">🔍</span>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    Aucune recommandation trouvée
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    Essayez de modifier vos filtres pour voir plus de résultats
                  </p>
                  <Button 
                    onClick={() => setFilters({
                      role: 'all',
                      location: 'all', 
                      profession: 'all',
                      experienceLevel: 'all',
                      verifiedOnly: false
                    })}
                    iconName="RefreshCw"
                  >
                    Réinitialiser les filtres
                  </Button>
                </div>
              )}
            </div>

            {/* Right Sidebar - Recommendation Tips */}
            <div className="lg:col-span-3">
              <RecommendationReasons />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConnectionRecommendations;